import assignmentService from 'ApiService/Teacher/Assignment';
import testService from 'ApiService/Teacher/TestService';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Stepper from 'views/admin/main/users/newUser/components/Stepper';
import StepperControl from 'views/admin/main/users/newUser/components/Subject/StepperControl';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';
import SelectAssignment from './Steps/SelectAssignment';
import SelectBatches from './Steps/SelectBatches';

const Index = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [testSchedule, setTestSchedule] = useState('daily');
  const [testData, setTestData] = useState({
    courseId: '',
  });
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [disable, setDisable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [scheduledAt, setscheduledAt] = useState('');
  const [isModalOpenConfirmation, setIsModalOpenConfirmation] = useState(false);

  const router = useNavigate();
  const { state } = useContext(AuthContext);
  const steps = [
    { stepNo: 1, name: 'Course Info' },
    { stepNo: 2, name: 'Add Details' },
  ];

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await testService.getAllCourses();
      if (response.data.success) {
        setCourses(response.data.courses);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleConfirmAssign = () => {
    if (!selectedTest || selectedBatches.length === 0) {
      toast.error('Please select a test and at least one batch.');
      return;
    }
    if (!scheduledAt) {
      toast.error('Please add scheduled Date');
      return;
    }
    setIsModalOpenConfirmation(true);
  };

  const handleAssignTest = async () => {
    if (loading) return;
    if (!selectedTest || !selectedBatches.length === 0 || !testSchedule) {
      toast.error('Please select a test and at least one batch.');
      return;
    }
    setLoading(true);
    const payload = {
      batches: selectedBatches,
      assignmentId: selectedTest,
      assignmentSchedule: testSchedule,
      scheduledAt: scheduledAt,
    };
    try {
      const response = await assignmentService.assignmentToBatches(payload);
      if (response.data.success) {
        toast.success('Assignment assigned successfully');
        setCurrentStep(1);
        setSelectedBatches([]);
        setTestSchedule('daily');
        setIsModalOpenConfirmation(false);
        setscheduledAt('');
        fetchCourses();
        setSelectedTest(null);
      } else {
        toast.error(response.data.message || 'Failed to assign test.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to assign test.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const displayStep = (step) => {
    switch (step.stepNo) {
      case 1:
        return (
          <SelectBatches
            setLoading={setLoading}
            setDisable={setDisable}
            courses={courses}
            setTestData={setTestData}
            testData={testData}
            loadingCourses={loadingCourses}
          />
        );
      case 2:
        return (
          <SelectAssignment
            setLoading={setLoading}
            setDisable={setDisable}
            testData={testData}
            selectedTest={selectedTest}
            setSelectedBatches={setSelectedBatches}
            setSelectedTest={setSelectedTest}
            selectedBatches={selectedBatches}
            setTestSchedule={setTestSchedule}
            testSchedule={testSchedule}
            setscheduledAt={setscheduledAt}
            scheduledAt={scheduledAt}
          />
        );
      default:
        return null;
    }
  };

  const handleNext = async () => {
    if (disable) return;
    setLoading(true);
    setCurrentStep(currentStep + 1);
    setLoading(false);
  };

  const handleBack = () => {
    if (disable) return;
    setCurrentStep((cs) => cs - 1);
  };

  const handleKeyDown = (event) => {
    if (disable) return;

    if (event.key === 'Enter') {
      if (currentStep === 1) {
        handleNext();
      }
    } else if (event.key === 'ArrowLeft') {
      if (currentStep > 1) {
        handleBack();
      }
    } else if (event.key === 'ArrowRight') {
      if (currentStep < steps.length) {
        handleNext();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentStep, disable]);

  const handleClick = (direction) => {
    let newStep = currentStep;

    direction === 'next' ? newStep++ : newStep--;
    // check if steps are within bounds
    newStep > 0 && newStep <= steps.length && setCurrentStep(newStep);
  };

  return (
    <div className="md-mt-[3] mt-3 h-full w-full">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />
      <div className="w-md:2/3 mx-auto h-full w-5/6 md:px-3  3xl:w-7/12">
        <div className="-mt-[280px] w-full pb-10 md:-mt-[340px] md:px-[70px]">
          <Stepper steps={steps} currentStep={currentStep} />
        </div>
        <Card extra={'h-full mx-auto pb-3'}>
          <div className="rounded-[20px]">
            <UseContextProvider>
              {displayStep(steps[currentStep - 1])}
            </UseContextProvider>
          </div>
          {/* navigation button */}
          <StepperControl
            handleClick={handleClick}
            currentStep={currentStep}
            steps={steps}
            loading={loading}
            disable={disable}
            handleSubmit={
              currentStep === steps.length ? handleConfirmAssign : handleClick
            }
          />
        </Card>
      </div>
      <ConfirmationModal
        isOpen={isModalOpenConfirmation}
        onClose={() => {
          if (!loading) setIsModalOpenConfirmation(false);
        }}
        onConfirm={handleAssignTest}
        title="Confirm Assign Assigment"
        message={`Are you sure you want to assign this assignment to ${selectedBatches.length} selected batch(es)?`}
        confirmText={loading ? 'Assigning...' : 'Yes, Assign Assigment'}
        disableConfirm={loading}
        cancelText="Cancel"
        icon="info"
      />
    </div>
  );
};

export default Index;
